import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import MemberDashboardModal from './MemberDashboardModal'
import { PlayingLevel, PlayingRangeBadge } from './PlayingLevel'
import { formatLongDate, formatTimeInTimeZone } from '../utils/golfTime'

function Avatar({ player }) {
  const [failed, setFailed] = useState(false)
  const name = player.name || ''
  return player.avatar_url && !failed ? <img className="mp-open-match-slot__avatar" src={player.avatar_url} alt="" onError={() => setFailed(true)} /> : <span className="mp-open-match-slot__avatar mp-open-match-slot__avatar--fallback" aria-hidden="true">{name.split(' ').map(part => part[0]).filter(Boolean).slice(0, 2).join('')}</span>
}
export default function GolfOpenGroupsSection({ clubCode, course, courseId, date, timeZone, onChanged }) {
  const { t, locale } = useOptionalPrivanaI18n()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [revision, setRevision] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const selected = searchParams.get('group')
  const openedHere = useRef(false)
  function openDetail(id) {
    dialogOwnerRef.current = null
    openedHere.current = true
    const next = new URLSearchParams(searchParams)
    next.set('group', id)
    setSearchParams(next)
  }
  function closeDetail() {
    dialogOwnerRef.current = null
    if (openedHere.current) { openedHere.current = false; navigate(-1); return }
    const next = new URLSearchParams(searchParams)
    next.delete('group')
    setSearchParams(next, { replace: true })
  }
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(false)
  const [detailRevision, setDetailRevision] = useState(0)
  // Mutation locks belong to the group, not to a particular opening of its dialog.
  // The ref also blocks two submissions before React commits the disabled control.
  const pendingActionsRef = useRef(new Map())
  const [pendingActions, setPendingActions] = useState({})
  const [actionError, setActionError] = useState(null)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const scope = `${clubCode}/${courseId}/${date}`
  const scopeRef = useRef(scope)
  const mountedRef = useRef(false)
  const dialogOwnerRef = useRef(null)
  const dialogGenerationRef = useRef(0)
  const discoveryRequestRef = useRef(0)
  const mutationKey = `${scope}/${selected}`
  const busy = Boolean(pendingActions[mutationKey])

  useLayoutEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      dialogOwnerRef.current = null
      discoveryRequestRef.current += 1
    }
  }, [])

  useLayoutEffect(() => {
    scopeRef.current = scope
    const owner = selected ? { scope, groupId: selected, generation: ++dialogGenerationRef.current } : null
    dialogOwnerRef.current = owner
    setDetail(null)
    setDetailLoading(Boolean(selected))
    setDetailError(false)
    setActionError(null)
    setConfirmLeave(false)
    return () => {
      if (dialogOwnerRef.current === owner) dialogOwnerRef.current = null
    }
  }, [scope, selected, location.key])

  function ownsDialog(owner) {
    return Boolean(owner && mountedRef.current && dialogOwnerRef.current === owner && scopeRef.current === owner.scope)
  }
  useEffect(() => {
    let current = true
    const request = ++discoveryRequestRef.current
    const isCurrent = () => current && mountedRef.current && request === discoveryRequestRef.current && scopeRef.current === scope
    setGroups([]); setError(false)
    if (!course?.open_groups_enabled || !date) { setLoading(false); return }
    setLoading(true)
    ;(async () => {
      try {
        const response = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses/${courseId}/open-groups?date=${date}`)
        const json = await response.json()
        if (!response.ok || json?.ok === false || !Array.isArray(json?.data)) throw new Error('load_failed')
        if (isCurrent()) setGroups(json.data)
      } catch { if (isCurrent()) setError(true) }
      finally { if (isCurrent()) setLoading(false) }
    })()
    return () => { current = false }
  }, [clubCode, courseId, date, course?.open_groups_enabled, memberPortalApiGet, revision])
  useEffect(() => {
    let current = true
    const owner = dialogOwnerRef.current
    if (!selected || !ownsDialog(owner)) return
    setDetail(null); setDetailError(false); setDetailLoading(true); setActionError(null); setConfirmLeave(false)
    ;(async () => {
      try {
        const response = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/bookings/${selected}/open-group`)
        const json = await response.json()
        if (!response.ok || json?.ok === false || !json?.data) throw new Error('load_failed')
        if (current && ownsDialog(owner)) setDetail(json.data)
      } catch { if (current && ownsDialog(owner)) setDetailError(true) }
      finally { if (current && ownsDialog(owner)) setDetailLoading(false) }
    })()
    return () => { current = false }
  }, [selected, clubCode, courseId, date, location.key, memberPortalApiGet, detailRevision])
  async function act(leave) {
    const owner = dialogOwnerRef.current
    if (!ownsDialog(owner) || !detail || detail.booking_id !== owner.groupId) return
    const key = `${owner.scope}/${owner.groupId}`
    if (pendingActionsRef.current.has(key)) return
    const mutation = {}
    pendingActionsRef.current.set(key, mutation)
    setPendingActions(previous => ({ ...previous, [key]: mutation }))
    setActionError(null)
    try {
      // Only the mutation can produce a join/withdrawal error. A failed refresh
      // must never present an already committed booking action as unsuccessful.
      try {
        const response = await memberPortalApiPost(`/api/members-portal/${clubCode}/golf/bookings/${owner.groupId}/${leave ? 'leave-open-group' : 'join-open-group'}`, {})
        const json = await response.json()
        if (!response.ok || json?.ok === false) throw new Error(json?.error || 'failed')
      } catch (err) {
        if (ownsDialog(owner)) {
          const errors = {
            already_joined: 'portalPresentation.member_you_already_joined_this_open_group',
            open_group_full: 'portalPresentation.member_this_open_group_is_full',
            join_not_allowed: 'portalPresentation.member_you_are_not_eligible_to_join_this_open_group',
            booking_not_found: 'portalPresentation.member_this_booking_is_no_longer_available',
          }
          setActionError(errors[err.message] || (leave ? 'portalPresentation.member_unable_to_leave_this_open_group_right_now' : 'portalPresentation.member_unable_to_join_this_open_group_right_now'))
        }
        return
      }

      // Completion belongs to the original server mutation even after close or
      // navigation. Notify the original availability owner immediately, then
      // refresh that discovery scope without writing into a different scope.
      onChanged?.()
      const request = mountedRef.current && scopeRef.current === owner.scope ? ++discoveryRequestRef.current : null
      const ownsDiscovery = () => mountedRef.current && scopeRef.current === owner.scope && request === discoveryRequestRef.current
      try {
        const response = await memberPortalApiGet(`/api/members-portal/${clubCode}/golf/courses/${courseId}/open-groups?date=${date}`)
        const json = await response.json()
        if (!response.ok || json?.ok === false || !Array.isArray(json?.data)) throw new Error('load_failed')
        if (ownsDiscovery()) { setGroups(json.data); setError(false) }
      } catch {
        if (ownsDiscovery()) setError(true)
      } finally {
        if (ownsDiscovery()) setLoading(false)
      }
      // Only the originating opening can schedule its follow-up detail read.
      // An already-started read also checks the owner before applying results.
      if (ownsDialog(owner)) setDetailRevision(value => value + 1)
    } finally {
      if (pendingActionsRef.current.get(key) === mutation) {
        pendingActionsRef.current.delete(key)
        if (mountedRef.current) setPendingActions(previous => {
          if (previous[key] !== mutation) return previous
          const next = { ...previous }
          delete next[key]
          return next
        })
      }
    }
  }
  if (!course?.open_groups_enabled) return null
  const range = group => <PlayingRangeBadge sport="golf" min={group.open_group_min_hcp} max={group.open_group_max_hcp} visible={group.show_handicap_range === true} />
  const badges = group => <div className="mp-open-match-card__chips"><span className="mp-chip subtle">{t('portalPresentation.member_open_group')}</span><span className="mp-chip subtle">{t(group.join_mode === 'request' ? 'portalPresentation.member_host_approval' : 'portalPresentation.member_direct_join')}</span>{range(group)}</div>
  return <section className="mp-golf-inline-groups" aria-labelledby="golf-open-groups-heading">
    <h3 id="golf-open-groups-heading">{t('portalPresentation.member_open_groups')}</h3>
    {loading ? <p role="status" className="mp-text-muted">{t('portalPresentation.member_loading_open_groups')}</p> : error ? <p role="alert">{t('portalPresentation.member_open_groups_load_failed')} <button type="button" className="mp-btn mp-btn--outline" onClick={() => setRevision(value => value + 1)}>{t('portalPresentation.member_events_retry')}</button></p> : groups.length === 0 ? <p className="mp-text-muted">{t('portalPresentation.member_no_open_groups_for_this_date_yet')}</p> : <div className="mp-open-group-grid">{groups.map(group => <button type="button" key={group.booking_id} className="mp-open-match-card mp-golf-discovery-card" onClick={() => openDetail(group.booking_id)}>
      <strong>{formatTimeInTimeZone(group.slot_time, timeZone, locale)} · {group.course_name || course.name}</strong>
      <span>{group.players_count} {t('portalPresentation.member_players_2')} · {t('portalPresentation.member_places_available_count', { count: group.spots_remaining })}</span>
      <span className="mp-open-group-card__avatars" aria-hidden="true">{(group.players || []).map((player, index) => <Avatar player={player} key={index} />)}</span>
      {badges(group)}
    </button>)}</div>}
    {selected ? <MemberDashboardModal labelledBy="golf-open-group-detail-title" closeLabel={t('portalPresentation.member_back')} onClose={closeDetail} screenKey={selected} size="compact" bodyClassName="mp-golf-group-detail">
      <button type="button" className="mp-btn mp-btn--ghost" onClick={closeDetail}>{t('portalPresentation.member_back')}</button>
      <h3 id="golf-open-group-detail-title">{t('portalPresentation.member_open_group')}</h3>
      <p>{formatLongDate(date, timeZone, locale)}{detail ? ` · ${formatTimeInTimeZone(detail.slot_time, timeZone, locale)}` : ''}</p>
      {detailLoading ? <p role="status">{t('portalPresentation.member_loading_open_groups')}</p> : detailError ? <p role="alert">{t('portalPresentation.member_open_group_details_failed')} <button type="button" className="mp-btn mp-btn--outline" onClick={() => setDetailRevision(value => value + 1)}>{t('portalPresentation.member_events_retry')}</button></p> : detail ? <>
        <h4>{detail.course_name}</h4>
        <p>{detail.holes} {t('portalPresentation.member_holes')} · {detail.players_count}/{detail.capacity} {t('portalPresentation.member_players_2')} · {t('portalPresentation.member_places_available_count', { count: detail.spots_remaining })}</p>
        {badges(detail)}
        <div className="mp-open-match-card__slots">{(detail.players || []).map((player, index) => <div className="mp-open-match-slot" key={`${player.member_id || 'guest'}-${index}`}><Avatar player={player} /><div className="mp-open-match-slot__meta"><span>{player.name || t('portalPresentation.member_member')}</span><small>{t(player.is_host ? 'portalPresentation.member_host' : player.type === 'guest' ? 'portalPresentation.member_guest' : 'portalPresentation.member_member')}</small><PlayingLevel sport="golf" value={player.handicap_index} /></div></div>)}{Array.from({ length: Math.max(0, Number(detail.spots_remaining) || 0) }, (_, index) => <div className="mp-open-match-slot is-empty" key={`empty-${index}`}>{t('portalPresentation.member_vacant_spot')}</div>)}</div>
        {detail.open_group_note ? <p className="mp-open-play-note">{detail.open_group_note}</p> : null}
        {actionError ? <p role="alert">{t(actionError)}</p> : null}
        <div className="mp-open-group-modal__actions">{detail.is_host ? <><p className="mp-text-muted">{t('portalPresentation.member_hosts_manage_booking')}</p><button type="button" className="mp-btn mp-btn--outline" onClick={() => navigate(`/${clubCode}/dashboard`)}>{t('portalPresentation.member_upcoming_bookings')}</button></> : detail.already_joined ? confirmLeave ? <><p>{t('portalPresentation.member_leave_group_confirm')}</p><button type="button" className="mp-btn mp-btn--outline" onClick={() => setConfirmLeave(false)}>{t('portalPresentation.member_keep_booking')}</button><button type="button" className="mp-btn mp-btn--primary" disabled={busy} onClick={() => act(true)}>{t('portalPresentation.member_leave')}</button></> : <button type="button" className="mp-btn mp-btn--outline" onClick={() => setConfirmLeave(true)}>{t('portalPresentation.member_leave')}</button> : <button type="button" className="mp-btn mp-btn--primary" disabled={busy || Number(detail.spots_remaining) <= 0 || detail.join_mode === 'request'} onClick={() => act(false)}>{t(Number(detail.spots_remaining) <= 0 ? 'portalPresentation.member_full' : 'portalPresentation.member_join')}</button>}</div>
      </> : null}
    </MemberDashboardModal> : null}
  </section>
}
